import React from "react";
import { useSelector } from "react-redux";
import { CssBaseline, Typography } from "@material-ui/core";
import * as UserDetailsAction from "../../stores/user/UserDetailsAction";
import { CenteredContainer } from "../components/CenteredContainer/CenteredContainer";
import ToastStatusEnum from "../../constants/ToastStatusEnum";
import { useSnackbar } from "notistack";
import { useDispatchWithError } from "../../hooks/useDispatchWithError";
import { useHistory, useLocation } from "react-router-dom";
import { PageBanner } from "../components/PageBanner/PageBanner";
import { RegisterFormWithButton } from "./registration/RegisterFormWithButton";
import { selectVisibleCountries } from "../../selectors/countries/CountriesSelector";

export interface ISaveUserDetailsViewProps {
}

export const SaveUserDetailsView: React.FunctionComponent<ISaveUserDetailsViewProps> = () => {
    const history = useHistory();
    const location = useLocation();
    const {enqueueSnackbar} = useSnackbar();
    const userId = location?.state?.userId;
    const email = location?.state?.email;
    const username = location?.state?.username;

    const [dispatchWithError, registerError] = useDispatchWithError(
        UserDetailsAction.REQUEST_SAVE_USER_DETAILS_FINISHED,
        () => {
            enqueueSnackbar("Details have been saved", {variant: ToastStatusEnum.Info});
            history.push("/save-user-details/success");
        }
    );

    const countries = useSelector(state => selectVisibleCountries(state));

    const [details, setDetails] = React.useState({
        firstName: "",
        lastName: "",
        jobPosition: "",
        phone: "",
        mobilePhone: "",
        company: "",
        tin: "",

        // honeypot
        website: "",

        addressLine1: "",
        addressLine2: "",
        postcode: "",
        city: "",
        country: "",

        deliveryAddressLine1: "",
        deliveryAddressLine2: "",
        deliveryPostcode: "",
        deliveryCity: "",
        deliveryCountry: ""
    });

    const createJSON = (details) => {
        return {
            email,
            username,
            firstName: details.firstName,
            lastName: details.lastName,
            phone: details.phone,
            mobilePhone: details.mobilePhone,
            billingAddress: {
                name: details.firstName + " " + details.lastName,
                addressLine1: details.addressLine1,
                addressLine2: details.addressLine2,
                postcode: details.postcode,
                city: details.city,
                country: details.country
            },
            deliveryAddress: {
                name: details.firstName + " " + details.lastName,
                addressLine1: details.deliveryAddressLine1,
                addressLine2: details.deliveryAddressLine2,
                postcode: details.deliveryPostcode,
                city: details.deliveryCity,
                country: details.deliveryCountry
            },
            company: details.company,
            tin: details.tin,
            jobPosition: details.jobPosition
        };
    };

    const [errors, setErrors] = React.useState([]);

    const handleChange = (event, valid) => {
        const name = event.target.name;
        const value = event.target.value;

        setDetails({
            ...details,
            [name]: value
        });

        if (valid) {
            setErrors(errors.filter(e => e !== name));
        } else {
            setErrors(errors.concat(name));
        };
    };

    const handleCountry = (event) => {
        if (event.target.value !== "") {
            setDetails({
                ...details,
                [event.target.name]: event.target.value
            });
        }
    }

    const [anotherDeliveryAddress, setAnotherDeliveryAddress] = React.useState(false);

    const saveUserDetails = event => {
        event.preventDefault();
        let sendDetails = details;
        if (anotherDeliveryAddress === false) {
            sendDetails.deliveryAddressLine1 = details.addressLine1;
            sendDetails.deliveryAddressLine2 = details.addressLine2;
            sendDetails.deliveryCity = details.city;
            sendDetails.deliveryPostcode = details.postcode;
            sendDetails.deliveryCountry = details.country;
        }
        if (details.website === "") {
            const userJSON = createJSON(sendDetails);
            dispatchWithError(UserDetailsAction.saveUserDetails(userId, userJSON));
        }
    };

    return <>
        <PageBanner title={"Save user details"} isPublic />
        <CenteredContainer>
            <CssBaseline />
            <Typography variant="h4" gutterBottom>
                Please provide the below mandatory fields required for Data Shop
            </Typography>
            <RegisterFormWithButton
                type="saveUserDetails"
                details={details}
                registerError={registerError}
                handleChange={handleChange}
                handleCountry={handleCountry}
                handleSubmit={saveUserDetails}
                allCountries={countries}
                errors={errors}
                anotherDeliveryAddress={anotherDeliveryAddress}
                setAnotherDeliveryAddress={setAnotherDeliveryAddress}
            />
        </CenteredContainer>
    </>;
};
